import React, { useEffect, useState } from 'react'
import { Button, ProgressBar } from 'react-bootstrap'
import { mbValue, isTrialAccount } from '../../../utils/general'
import { ACTION_CREATE } from '../interface'
import { FeatureCard } from '../../FeatureCard'
import { FEATURE_TYPES, FEATURE_CARD_TITLE } from '../../FeatureCard/features'
import { WORKING_MAGIC_MESSAGE } from '../../../constants/messages'
import { isEligibleForSoloTrial } from '../../../utils/free-trial'
import FreeTrialModalContainer from '../../FreeTrialModal/FreeTrialModalContainer'
import styles from '../../FeatureCard/FeatureModal.module.css'

import './LoadingBox.css'

let progressInterval = null

const getRandomFeatureType = () => {
  const randomIndex = Math.floor(Math.random() * FEATURE_TYPES.length)
  return FEATURE_TYPES[randomIndex]
}

export const getSimulationDuration = (fileSize) => {
  if (fileSize < mbValue(100)) {
    return 300
  } else if (fileSize >= mbValue(100) && fileSize < mbValue(500)) {
    return 1000
  } else if (fileSize >= mbValue(500)) {
    return 1500
  }
}

export const SimulatedProgressBar = ({ start, duration = 300, progressBarMessage, action }) => {
  const [progress, setProgress] = useState(start)
  const [isCompleted, setIsCompleted] = useState(false)
  const message = action === ACTION_CREATE ? 'Creating site, please wait...' : 'Updating site, please wait...'

  useEffect(() => {
    if (!isCompleted) {
      progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 99) {
            setIsCompleted(true)
            clearInterval(progressInterval)
            return 99
          }
          return prev + 1
        })
      }, duration)
    }

    return () => {
      if (progressInterval) {
        clearInterval(progressInterval)
      }
    }
  }, [duration, isCompleted])

  return <ProgressBarLoader now={progress} progressBarMessage={progress >= 99 ? message : progressBarMessage} />
}

const ProgressBarLoader = ({ className = '', progressBarMessage = WORKING_MAGIC_MESSAGE, now }) => {
  return (
    <div className={`progress-bar-loader ${className}`}>
      <div className="mb-3">{progressBarMessage}</div>
      <ProgressBar animated now={now} label={`${now}%`} />
    </div>
  )
}

export const LoadingBox = ({
  progress,
  message = WORKING_MAGIC_MESSAGE,
  userProfile,
  onComplete,
  dispatch,
  liveSites = [],
  isPdfConversion = false
}) => {
  const [randomFeatureType] = useState(() => getRandomFeatureType())
  const [displayProgress, setDisplayProgress] = useState(progress)
  const [showFreeTrialModal, setShowFreeTrialModal] = useState(false)
  const [trialModalDismissed, setTrialModalDismissed] = useState(false)
  const isFreeUser = isTrialAccount(userProfile)

  const isEligible = isFreeUser && isEligibleForSoloTrial(userProfile)

  useEffect(() => {
    // For PDF conversion, use progress directly without simulation
    if (isPdfConversion) {
      setDisplayProgress(progress)
      if (progress >= 100 && !isFreeUser && onComplete) {
        const timer = setTimeout(() => {
          onComplete()
        }, 500)
        return () => clearTimeout(timer)
      }
      return
    }

    // Normal upload progress handling
    if (progress >= 100) {
      setDisplayProgress(100)
      const timer = setTimeout(() => {
        if (!isFreeUser && onComplete) {
          onComplete()
        }
      }, 500)
      return () => clearTimeout(timer)
    }

    if (progress >= 50 && progress < 100) {
      const interval = setInterval(() => {
        setDisplayProgress((prev) => {
          if (prev >= 99) {
            clearInterval(interval)
            return 99
          }
          return prev + 1
        })
      }, 200)
      return () => clearInterval(interval)
    }

    setDisplayProgress(progress)
  }, [progress, onComplete, isFreeUser, isPdfConversion])

  useEffect(() => {
    if (!isFreeUser && progress >= 100 && onComplete) {
      onComplete()
    }
  }, [progress, isFreeUser, onComplete])

  useEffect(() => {
    if (displayProgress >= 100 && isEligible) {
      setShowFreeTrialModal(true)

      localStorage.removeItem('freeTrialModalDismissedForUpload')
    }
  }, [displayProgress, isEligible, showFreeTrialModal])

  useEffect(() => {
    if (!showFreeTrialModal) return

    const checkDismissal = setInterval(() => {
      const wasDismissed = localStorage.getItem('freeTrialModalDismissedForUpload') === 'true'
      if (wasDismissed && !trialModalDismissed) {
        localStorage.removeItem('freeTrialModalDismissedForUpload')
        setTrialModalDismissed(true)
        if (onComplete) {
          onComplete()
        }
      }
    }, 100)

    return () => clearInterval(checkDismissal)
  }, [showFreeTrialModal, trialModalDismissed, onComplete])

  const handleContinueClick = () => {
    if (onComplete) {
      onComplete()
    }
  }

  return (
    <>
      {displayProgress >= 100 && isEligible ? (
        <FreeTrialModalContainer
          userProfile={userProfile}
          liveSites={liveSites}
          dispatch={dispatch}
          setFirstTimeSuccModal={() => {}}
          shouldShowOnUploadUpdate={true}
          hideBanner={true}
        />
      ) : (
        <div className="loading-box">
          {progress < 100 && (
            <div className={styles.modalHeader}>
              <div className={styles.headerLeft}>
                <img src="/assets/icons/progressBarIcons/Loading.svg" alt="Loading" className={styles.loadingIcon} />
                <span className={styles.loadingText}>{message}</span>
              </div>
              <span className={styles.progressPercentage}>{Math.min(Math.round(displayProgress), 100)}%</span>
            </div>
          )}

          {displayProgress < 100 ? (
            <div className={styles.progressContainer}>
              <ProgressBar animated now={displayProgress} className={styles.customProgressBar} />
            </div>
          ) : (
            <div className={styles.completeButtonContainer}>
              <Button className="w-100" onClick={handleContinueClick} data-testid="LoadingBox-button-continue">
                Continue
              </Button>
            </div>
          )}

          <FeatureCard featureType={randomFeatureType} mainTitle={FEATURE_CARD_TITLE} />
        </div>
      )}
    </>
  )
}
